import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { PageContainer } from '@/components/ui/PageContainer';
import { ClientsService, type ClientDTO } from '@/services/ClientsService';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { useNotificationStore } from '@/stores/notificationStore';
import { Building2, Calendar, ArrowLeft, Mail, Phone, Pencil, ChevronRight, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ClientDashboard() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const clientsSvc = useMemo(() => new ClientsService(), []);
  const eventsSvc = useMemo(() => new EventsService(), []);
  const notifyError = useNotificationStore(s => s.error);
  const notifySuccess = useNotificationStore(s => s.success);
  
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<ClientDTO | null>(null);
  const [events, setEvents] = useState<EventDTO[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<ClientDTO>({ id: '', name: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  
  // Event modal state
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState<{ name: string; description?: string }>({ name: '' });
  const [eventSaving, setEventSaving] = useState(false);

  // Validate email
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!form.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (form.contact_email && !validateEmail(form.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form field changes with validation
  const handleFieldChange = (field: keyof ClientDTO, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(e => ({ ...e, [field]: '' }));
    }
    
    // Validate email on change
    if (field === 'contact_email' && value) {
      if (!validateEmail(value)) {
        setFormErrors(e => ({ ...e, contact_email: 'Please enter a valid email address' }));
      }
    }
  };

  function openEdit() {
    if (!client) return;
    setForm({ ...client });
    setFormErrors({});
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setForm({ id: '', name: '' });
    setFormErrors({});
  }

  async function submit() {
    if (!client || !validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      await clientsSvc.update(client.id, { 
        name: form.name, 
        contact_email: form.contact_email, 
        contact_name: form.contact_name, 
        contact_phone: form.contact_phone, 
        notes: form.notes 
      });
      notifySuccess('Client updated successfully');
      closeModal();
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function openNewEvent() {
    setEventForm({ name: '', description: '' });
    setEventModalOpen(true);
  }

  function closeEventModal() {
    setEventModalOpen(false);
    setEventForm({ name: '', description: '' });
  }

  async function submitEvent() {
    if (!clientId || !eventForm.name.trim()) return;
    
    setEventSaving(true);
    try {
      const result = await eventsSvc.create(clientId, {
        name: eventForm.name,
        description: eventForm.description || undefined,
      });
      notifySuccess('Event created successfully');
      closeEventModal();
      await load();
      // Navigate to the new event
      navigate(`/organization/events/${result.id}`);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to create event');
    } finally {
      setEventSaving(false);
    }
  }

  async function load() {
    if (!clientId) return;
    
    setLoading(true);
    try {
      const [clientData, eventsData] = await Promise.all([
        clientsSvc.get(clientId),
        eventsSvc.listByClient(clientId)
      ]);
      setClient(clientData);
      setEvents(eventsData);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load client data');
      navigate('/organization/clients');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [clientId]);


  if (loading && !client) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (!client) return null;

  const hasContactInfo = !!(client.contact_name || client.contact_email || client.contact_phone);
  const hasNotes = !!client.notes;

  return (
    <PageContainer>
      <PageHeader 
        title={client.name}
        description="Client overview and management"
      >
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/organization/clients')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Clients
          </Button>
          <Button 
            size="sm"
            onClick={openEdit}
            leftIcon={<Pencil className="w-4 h-4" />}
          >
            Edit Client
          </Button>
        </div>
      </PageHeader>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Client Details */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-0">
              {hasContactInfo || hasNotes ? (
                <div className="p-6">
                  {/* Contact Information */}
                  {hasContactInfo && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-3">Contact Information</h3>
                      <div className="space-y-3">
                        {client.contact_name && (
                          <div className="flex items-center gap-3 text-gray-900">
                            <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{client.contact_name}</span>
                          </div>
                        )}
                        {client.contact_email && (
                          <div className="flex items-center gap-3 text-gray-900">
                            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <a 
                              href={`mailto:${client.contact_email}`} 
                              className="text-primary-600 hover:underline"
                            >
                              {client.contact_email}
                            </a>
                          </div>
                        )}
                        {client.contact_phone && (
                          <div className="flex items-center gap-3 text-gray-900">
                            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{client.contact_phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Notes */}
                  {hasNotes && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
                      <p className="text-gray-900 whitespace-pre-wrap">{client.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 p-6">
                  <p className="text-gray-500 mb-3">No client information available.</p>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={openEdit}
                    leftIcon={<Pencil className="w-4 h-4" />}
                  >
                    Add Information
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Client Events */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Events</h2>
            <Button 
              size="sm"
              onClick={openNewEvent}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              New Event
            </Button>
          </div>
          
          {events.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No events yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Create your first event for this client
                </p>
                <Button 
                  onClick={openNewEvent}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  New Event
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <button
                  key={event.id}
                  onClick={() => navigate(`/organization/events/${event.id}`)}
                  className={cn(
                    "relative flex items-center gap-4 p-4 rounded-lg border text-left transition-all w-full",
                    "bg-white border-gray-200 hover:border-primary-300 hover:shadow-md cursor-pointer"
                  )}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{event.name}</h3>
                    {event.description && (
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-1">{event.description}</p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Client Modal */}
      <Modal 
        isOpen={modalOpen} 
        onClose={closeModal} 
        title="Edit Client"
        description="Update client information"
      >
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Company Name *</label>
            <Input 
              value={form.name} 
              onChange={(e) => handleFieldChange('name', e.target.value)} 
              placeholder="e.g., ACME Corporation"
              error={formErrors.name}
              required
            />
            {formErrors.name && (
              <p className="text-xs text-error-600 mt-1">{formErrors.name}</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contact Name</label>
              <Input 
                value={form.contact_name || ''} 
                onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} 
                placeholder="e.g., John Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Email</label>
              <Input 
                type="email"
                value={form.contact_email || ''} 
                onChange={(e) => handleFieldChange('contact_email', e.target.value.replace(/\s/g, ''))} 
                placeholder="e.g., john@acme.com"
                error={formErrors.contact_email}
              />
              {formErrors.contact_email && (
                <p className="text-xs text-error-600 mt-1">{formErrors.contact_email}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Contact Phone</label>
            <PhoneInput 
              value={form.contact_phone || ''} 
              onChange={(value) => handleFieldChange('contact_phone', value)} 
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <Textarea 
              value={form.notes || ''} 
              onChange={(e) => handleFieldChange('notes', e.target.value)} 
              placeholder="Any additional information..."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              isLoading={saving}
              disabled={!form.name.trim() || Object.keys(formErrors).some(k => formErrors[k])}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* New Event Modal */}
      <Modal 
        isOpen={eventModalOpen} 
        onClose={closeEventModal} 
        title="New Event"
        description="Create a new event for this client"
      >
        <form onSubmit={(e) => { e.preventDefault(); submitEvent(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Event Name *</label>
            <Input 
              value={eventForm.name} 
              onChange={(e) => setEventForm(f => ({ ...f, name: e.target.value }))} 
              placeholder="e.g., 2024 Annual Company Retreat"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Textarea 
              value={eventForm.description || ''} 
              onChange={(e) => setEventForm(f => ({ ...f, description: e.target.value }))} 
              placeholder="Brief description of the event"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={closeEventModal} disabled={eventSaving}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              isLoading={eventSaving}
              disabled={!eventForm.name.trim()}
            >
              Create Event
            </Button>
          </div>
        </form>
      </Modal>
    </PageContainer>
  );
}

